import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  Ban,
  DollarSign,
  PackageCheck,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import AttachmentsListView from '../components/AttachmentsListView';
import VoidConfirmationModal from '../components/VoidConfirmationModal';
import { getBillById, receiveBill, voidBill } from '../utils/billApi';
import { canEditBill } from '../utils/billEditability';
import { getBillAttachments } from '../utils/itemAttachmentApi';
import type { Bill, BillStatus } from '../types/bill';
import type { Attachment } from '../types/attachment';
import { formatCurrency, formatPreviewQuantity } from '../utils/formatCurrency';
import { toast } from '../utils/toast';
import { billVoidCopy } from '../utils/voidConfirmationCopy';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const canManageBills = () => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

const canDeleteOrVoid = () => localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

const getStatusColor = (status: BillStatus) => {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const canPayBill = (status: BillStatus) => status === 'OPEN' || status === 'PARTIAL';

const vendorName = (bill: Bill) =>
  bill.vendor_snapshot.name || bill.vendor?.display_name || '—';

const PurchaseView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bill, setBill] = useState<Bill | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);

  const billId = Number(id);

  const fetchBill = async () => {
    if (!id || Number.isNaN(billId)) {
      setError('Invalid bill ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [data, billAttachments] = await Promise.all([
        getBillById(billId),
        getBillAttachments(billId).catch((err) => {
          console.error('[PurchaseView] Failed to load attachments', err);
          return [];
        }),
      ]);
      setBill(data);
      setAttachments(billAttachments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bill');
      setBill(null);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBill();
  }, [id]);

  const handleReceive = async () => {
    if (!bill || !canManageBills()) return;
    if (!window.confirm(`Receive bill ${bill.bill_number} into inventory and post GL?`)) return;

    try {
      setActionLoading(true);
      await receiveBill(bill.id);
      const [updatedBill, billAttachments] = await Promise.all([
        getBillById(bill.id),
        getBillAttachments(bill.id).catch(() => []),
      ]);
      setBill(updatedBill);
      setAttachments(billAttachments);
      toast.success(`Bill ${bill.bill_number} received.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to receive bill');
    } finally {
      setActionLoading(false);
    }
  };

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async () => {
    if (!bill || !canDeleteOrVoid()) return;

    try {
      setVoiding(true);
      setVoidError(null);
      await voidBill(bill.id);
      const [updatedBill, billAttachments] = await Promise.all([
        getBillById(bill.id),
        getBillAttachments(bill.id).catch(() => []),
      ]);
      setBill(updatedBill);
      setAttachments(billAttachments);
      setShowVoidModal(false);
      toast.success(`Bill ${bill.bill_number} voided.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to void bill';
      setVoidError(message);
      toast.error(message);
    } finally {
      setVoiding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
        </main>
      </div>
    );
  }

  if (error || !bill) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-red-500 mb-3" />
            <p className="text-gray-900 font-medium mb-2">Could not load bill</p>
            <p className="text-sm text-gray-500 mb-4">{error || 'Bill not found.'}</p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={fetchBill}
                className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
              >
                Retry
              </button>
              <Link
                to="/purchases"
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Back to Bills
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-hidden">
        <div className="w-full max-w-full">
          <div className="mb-6">
            <Link
              to="/purchases"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Bills
            </Link>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                  {bill.bill_number}
                </h1>
                <div className="flex gap-2 mt-2">
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(bill.status)}`}
                  >
                    {formatLabel(bill.status)}
                  </span>
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                      bill.received_into_inventory
                        ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {bill.received_into_inventory ? 'Received' : 'Not received'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm mb-6 max-w-xl">
              <h2 className="font-semibold text-gray-900 mb-3">Vendor & Dates</h2>
              <div className="flex justify-between">
                <span className="text-gray-600">Vendor</span>
                <span className="font-medium">{vendorName(bill)}</span>
              </div>
              {bill.vendor_snapshot.email && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Email</span>
                  <span>{bill.vendor_snapshot.email}</span>
                </div>
              )}
              {bill.vendor_snapshot.phone && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone</span>
                  <span>{bill.vendor_snapshot.phone}</span>
                </div>
              )}
              {bill.vendor_snapshot.address && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Address</span>
                  <span className="text-right max-w-[60%]">{bill.vendor_snapshot.address}</span>
                </div>
              )}
              {bill.vendor_bill_number && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Vendor invoice no</span>
                  <span className="font-medium">{bill.vendor_bill_number}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Bill date</span>
                <span>{formatDate(bill.txn_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Due date</span>
                <span>{formatDate(bill.due_date)}</span>
              </div>
            </div>

            <div className="mb-6 overflow-x-auto">
              <h2 className="font-semibold text-gray-900 mb-3">Line Items</h2>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Disc %</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Line Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(bill.lines ?? []).map((line) => (
                    <tr key={line.id}>
                      <td className="px-4 py-2">
                        <div className="font-medium">
                          {line.item_snapshot?.name ?? line.notes ?? 'Expense'}
                        </div>
                        {line.item_snapshot?.sku && (
                          <div className="text-xs text-gray-500 font-mono">{line.item_snapshot.sku}</div>
                        )}
                        {line.description && (
                          <div className="text-xs text-gray-600 mt-1 whitespace-pre-line">{line.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">{formatPreviewQuantity(line.qty)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(line.unit_price)}</td>
                      <td className="px-4 py-2 text-right">{line.discount_percent}%</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(line.tax_amount)}</td>
                      <td className="px-4 py-2 text-right font-medium">{formatCurrency(line.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-6">
              <div className="w-full sm:w-80 bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(bill.sub_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount</span>
                  <span className="font-medium">{formatCurrency(bill.discount_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium">{formatCurrency(bill.tax_total)}</span>
                </div>
                <div className="flex justify-between font-semibold border-t border-gray-200 pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(bill.total_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Balance</span>
                  <span
                    className={
                      parseFloat(bill.balance) > 0
                        ? 'text-red-600 font-semibold'
                        : 'text-green-600 font-semibold'
                    }
                  >
                    {formatCurrency(bill.balance)}
                  </span>
                </div>
              </div>
            </div>

            {bill.memo && (
              <p className="text-sm text-gray-600 mb-4">
                <span className="font-medium">Memo:</span> {bill.memo}
              </p>
            )}

            {(bill.cogs_true_ups?.length ?? 0) > 0 && (
              <div className="mb-6 overflow-x-auto">
                <h2 className="font-semibold text-gray-900 mb-2">COGS true-up</h2>
                <p className="text-sm text-gray-600 mb-3">
                  Receiving this bill cleared oversell backorders and adjusted COGS where the receive
                  cost differed from the sale-time estimate. Adjustments post on the receive date.
                </p>
                <table className="min-w-full divide-y divide-gray-200 text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Invoice
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        SKU
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Qty cleared
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Est. unit cost
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Actual unit cost
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        COGS adjustment
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {bill.cogs_true_ups!.map((row) => (
                      <tr key={`${row.bill_line_id}-${row.invoice_line_id}`}>
                        <td className="px-4 py-2 font-medium text-slate-800">{row.invoice_number}</td>
                        <td className="px-4 py-2 font-mono text-gray-700">{row.item_sku}</td>
                        <td className="px-4 py-2 text-right font-mono">{formatPreviewQuantity(row.qty_cleared)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(row.estimated_unit_cost)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(row.actual_unit_cost)}</td>
                        <td
                          className={`px-4 py-2 text-right font-medium ${
                            parseFloat(row.cogs_adjustment_amount) >= 0
                              ? 'text-red-700'
                              : 'text-green-700'
                          }`}
                        >
                          {formatCurrency(row.cogs_adjustment_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <AttachmentsListView attachments={attachments} />

            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
              {canEditBill(bill) && canManageBills() && (
                <button
                  type="button"
                  onClick={() => navigate(`/manage-purchases?id=${bill.id}`)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  {bill.status === 'DRAFT' ? 'Edit Draft' : 'Amend Bill'}
                </button>
              )}
              {bill.status === 'DRAFT' && !bill.received_into_inventory && canManageBills() && (
                <button
                  type="button"
                  onClick={handleReceive}
                  className="flex items-center px-4 py-2 bg-green-700 text-white rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  <PackageCheck className="h-4 w-4 mr-2" />
                  Receive Bill
                </button>
              )}
              {bill.status === 'OPEN' && bill.received_into_inventory && canDeleteOrVoid() && (
                <button
                  type="button"
                  onClick={openVoidModal}
                  className="flex items-center px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm"
                  disabled={actionLoading || voiding}
                >
                  <Ban className="h-4 w-4 mr-2" />
                  Void
                </button>
              )}
              {canPayBill(bill.status) && (
                <button
                  type="button"
                  onClick={() =>
                    navigate('/pay-bills', {
                      state: { vendorId: bill.vendor_id, billId: bill.id },
                    })
                  }
                  className="flex items-center px-4 py-2 bg-slate-800 text-white rounded-lg text-sm"
                  disabled={actionLoading}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Pay Bill
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      {bill && (
        <VoidConfirmationModal
          isOpen={showVoidModal}
          {...billVoidCopy(bill.bill_number)}
          isSubmitting={voiding}
          error={voidError}
          onCancel={cancelVoidModal}
          onConfirm={confirmVoid}
        />
      )}
    </div>
  );
};

export default PurchaseView;
